/**
 * Invoice & Payment Tracking — design tokens and shared preferences.
 *
 * The palette is the NFA module's, reused rather than re-specified, so the two
 * modules cannot drift. IPT adds only what it needs: a tone per lifecycle
 * stage, a tone per attribution party, and the two global metric toggles.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNfaTheme, money, day, stamp } from '@/components/nfa/nfaTheme';

export const useIptTheme = useNfaTheme;
export { money, day, stamp };

export const MAIN_STAGES = ['RECEIVED', 'CERTIFIED', 'SEN_GRN_POSTED', 'VERIFIED',
  'PRE_AUDITED', 'APPROVED', 'PAID'];

export const STAGE_LABEL = {
  RECEIVED: 'Received', CERTIFIED: 'Certified', SEN_GRN_POSTED: 'SEN/GRN posted',
  VERIFIED: 'Finance verified', PRE_AUDITED: 'Pre-audited', APPROVED: 'Approved',
  PAID: 'Paid', RETURNED: 'Returned to vendor', ON_HOLD: 'On hold',
  DISPUTED: 'Disputed', PARTIALLY_PAID: 'Partially paid', REJECTED: 'Rejected',
};

export const STAGE_TONE = {
  RECEIVED: 'muted', CERTIFIED: 'primary', SEN_GRN_POSTED: 'primary',
  VERIFIED: 'primary', PRE_AUDITED: 'primary', APPROVED: 'green', PAID: 'green',
  PARTIALLY_PAID: 'amber', RETURNED: 'accent', ON_HOLD: 'amber',
  DISPUTED: 'red', REJECTED: 'red',
};

/** Attribution parties. Deliberately NOT the suspension palette: the two axes
 *  must not read as the same encoding on the same chart. */
export const PARTY = {
  CLIENT: { label: 'Client', short: 'Us', tone: 'primary' },
  VENDOR: { label: 'Vendor', short: 'Vendor', tone: 'accent' },
  NEITHER: { label: 'Neither party', short: 'Third party', tone: 'muted' },
};

export const toneColor = (T, tone) => ({
  muted: T.muted, primary: T.primary, amber: T.amber, green: T.green,
  red: T.red, accent: T.accent,
}[tone] || T.muted);

export const num = (v, dp = 1) => (v === null || v === undefined || Number.isNaN(Number(v))
  ? '—' : Number(v).toFixed(dp));

export const int = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('en-IN'));

/* ── global metric preferences ─────────────────────────────────────────────
 * Working-day basis and the stop clock are module-wide, so they live outside
 * React state and are broadcast, letting the detail route (a separate page)
 * read the same choice the register was showing.
 */
const KEY = 'ipt_prefs_v1';
const DEFAULTS = { dayBasis: 'calendar', stopClock: true };
const read = () => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || '{}') }; }
  catch { return { ...DEFAULTS }; }
};

export const useIptPrefs = () => {
  const [prefs, setPrefs] = useState(read);
  useEffect(() => {
    const sync = () => setPrefs(read());
    window.addEventListener('ipt-prefs', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('ipt-prefs', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const set = useCallback((patch) => {
    const next = { ...read(), ...patch };
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('ipt-prefs'));
  }, []);
  /** Query string every metric call must carry, so a figure and the rule that
   *  produced it travel together. */
  const query = `day_basis=${prefs.dayBasis}&stop_clock=${prefs.stopClock}`;
  return { ...prefs, set, query };
};

export default useIptTheme;
